import { catchError, map, finalize, tap } from 'rxjs/operators';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { DataServiceBase } from '../../../core/service/base/data.service.base';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { DataService } from '../../../core/service/base/data.service';
import { FeeChangePreview } from '../../model/fee-change-preview';
import { FeeChange } from '../../model/fee-change';

export enum ExistingRowStrategy {
  Ignore = 0,
  Void = 1,
  AdjustEndDate = 2,
  SkipNew = 3,
}

@Injectable()
export class FeeChangeService extends DataService<FeeChange> {
  constructor(
    private _client: HttpClient,
    private _messageService: MessageService,
    progressBar: ProgressBarService,
    @Inject(APP_CONFIG) config: IAppConfig
  ) {
    super(_client, config, _messageService, FeeChange, progressBar, 'fee-change');
  }

  revert(id: string): Observable<void> {
    this.progressBar.start();
    return this.client.post(`${this.endpoint}/revert/${id}`, null).pipe(
      map(x => {}),
      finalize(() => this.progressBar.stop())
    );
  }

  upload(
    file: File,
    caption: string,
    conflictResolution: ExistingRowStrategy,
    createLeaseAgreementRows: boolean,
    createTemplateRows: boolean
  ) {
    const formData = new FormData();
    formData.append('jsonData', JSON.stringify({ caption, conflictResolution, createLeaseAgreementRows, createTemplateRows }));
    formData.append('file', file, file.name);
    this.progressBar.start();
    return this.client.post<string>(`${this.endpoint}/upload`, formData).pipe(
      tap(() => this._messageService.showSuccess()),
      catchError(err => {
        this._messageService.showError(err);
        throw err;
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  changeRental(form: any): Observable<any> {
    return this.client.put(`${this.endpoint}/rental-changes`, JSON.stringify(form));
  }

  previewRentalChanges(form: any): Observable<FeeChangePreview> {
    return this.client.post<FeeChangePreview>(`${this.endpoint}/preview-rental-changes`, JSON.stringify(form));
  }

  getRentalChangesRoundPeriodRentDefault(rentalObjects: string[]): Observable<boolean> {
    return this.client
      .post<{ roundPeriodRent: boolean }>(`${this.endpoint}/rental-changes/defaults`, { rentalObjects })
      .pipe(map(model => model.roundPeriodRent));
  }

  getExcelLeaseAgreements(id: string): Observable<File> {
    this.progressBar.start();
    return this._client
      .post(`${this.endpoint}/fee-change-leaseagreement-rows-excel/${id}`, null, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, 'Hyresjustering.xlsx');
        }),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  getExcelTemplateRows(id: string): Observable<File> {
    this.progressBar.start();
    return this._client
      .post(`${this.endpoint}/fee-change-template-rows-excel/${id}`, null, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        map((res: HttpResponse<Blob>) => {
          return this.toFile(res, 'Hyresjustering.xlsx');
        }),
        catchError(error => {
          this._messageService.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  getFilterDefinition(): Observable<{
    caption: string;
    changeType: number;
    selectedFields: string[];
    retroactive: boolean;
    reverted: boolean;
  }> {
    return this.client.get<any>(`${this.endpoint}/filter`).pipe(
      map(res => {
        return {
          caption: res.filter.caption ? res.filter.caption : '',
          changeType: res.filter.changeType,
          retroactive: res.filter.retroactive,
          reverted: res.filter.reverted,
          selectedFields: Array.isArray(res.selectedFields) ? res.selectedFields : ['caption', 'changeType', 'retroactive', 'reverted'],
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }
}
